import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';

import { CurrentCustomClaimsService } from '@padspin/ui-rbac';

import { HomerChatFabComponent } from './homer-chat-fab.component';
import { HomerChatService } from './homer-chat.service';

/**
 * Always-mounted, near-zero-cost shell for the Homer widget. Its only eager
 * dependencies are the Router-based {@link HomerChatService} and the current
 * user's claims; the FAB, panel, and all their Firestore/Genkit/Remote-Config
 * plumbing live in the `@defer` chunk and load only once the browser has gone
 * idle after first paint — the same "never compete with first paint" pattern as
 * the header's Twilio Voice panel. The `@defer (when …)` condition can only flip
 * in the browser, so nothing is baked into the prerendered/SSR HTML.
 *
 * While Homer is in closed beta the widget is gated on the `homer_beta` custom
 * claim; everyone else (signed out, anonymous, or without the claim) never loads
 * the deferred chunk nor its anonymous sign-in.
 */
@Component({
  selector: 'padspin-homer-chat-outlet',
  imports: [HomerChatFabComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @defer (when shouldLoad()) {
      <!--
      @defer is one-way: once shouldLoad() flips true it never tears the block
      down. So gate the FAB on enabled() here too, or a route that later sets
      data.homerDisabled (or calls disable()) couldn't hide an already-loaded FAB.
    -->
      @if (enabled()) {
        <padspin-homer-chat-fab />
      }
    }
  `,
})
export class HomerChatOutletComponent {
  private readonly document = inject(DOCUMENT);
  private readonly homerChat = inject(HomerChatService);
  private readonly claims = inject(
    CurrentCustomClaimsService,
  ).currentCustomClaims();
  private readonly settled = signal(false);

  private readonly inBeta = computed(() => this.claims()?.homer_beta === true);

  protected readonly shouldLoad = computed(
    () => this.settled() && this.homerChat.enabled() && this.inBeta(),
  );

  /** Reactive visibility gate re-checked after the deferred block has loaded. */
  protected readonly enabled = computed(
    () => this.homerChat.enabled() && this.inBeta(),
  );

  constructor() {
    afterNextRender(() => {
      const win = this.document.defaultView as
        | (typeof window & { requestIdleCallback?: (cb: () => void) => void })
        | null;
      if (!win) return;
      const whenIdle = win.requestIdleCallback ?? ((cb: () => void) => cb());
      whenIdle(() => win.setTimeout(() => this.settled.set(true), 2500));
    });
  }
}
